import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { z } from "zod"

import { EUgcTemplateHookType } from "@aix/vidmax-shared/src/constants/ugcTemplates"

import { TemplateCard } from "~/components/UGCWorkspace/TemplateGallery"
import type { IUGCTemplate } from "~/components/UGCWorkspace/types"
import AppLayout from "~/layouts/AppLayout"
import { api } from "~/utils/api"

const ugcTemplatesSearchSchema = z
  .object({
    hookType: z.nativeEnum(EUgcTemplateHookType).optional(),
  })
  .partial()

const HOOK_TYPE_LABELS: Readonly<Record<EUgcTemplateHookType, string>> = {
  [EUgcTemplateHookType.PAIN]: "Pain",
  [EUgcTemplateHookType.OUTCOME]: "Outcome",
  [EUgcTemplateHookType.CURIOSITY]: "Curiosity",
  [EUgcTemplateHookType.TESTIMONIAL]: "Testimonial",
  [EUgcTemplateHookType.UNBOXING]: "Unboxing",
}

function UGCTemplatesPage() {
  const navigate = useNavigate()
  const search: { hookType?: EUgcTemplateHookType } = useSearch({ from: "/create/ugc-templates" })
  const { data, isLoading } = api.ugcTemplates.list.useQuery()
  const [isMuted, setIsMuted] = useState(true)

  const filtered = useMemo(() => {
    const all = data?.templates ?? []
    return search.hookType ? all.filter((t) => t.hookType === search.hookType) : all
  }, [data?.templates, search.hookType])

  const handleSelect = (template: IUGCTemplate) => {
    void navigate({ to: "/create/ugc", search: { template: template.id } } as never)
  }

  const heading = search.hookType ? HOOK_TYPE_LABELS[search.hookType] : "All trending templates"

  const setHookType = (next: EUgcTemplateHookType | undefined) => {
    void navigate({
      to: "/create/ugc-templates",
      search: next ? { hookType: next } : {},
    } as never)
  }

  return (
    <AppLayout navCollapsible="icon" navIconFloat>
      <div className="flex flex-col gap-5 p-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight">{heading}</h1>
          <p className="text-sm text-muted-foreground">
            Click a template to start a new UGC video with its avatar, voice, and script prefilled.
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={!search.hookType} onClick={() => setHookType(undefined)}>
            All
          </FilterChip>
          {Object.values(EUgcTemplateHookType).map((ht) => (
            <FilterChip key={ht} active={search.hookType === ht} onClick={() => setHookType(ht)}>
              {HOOK_TYPE_LABELS[ht]}
            </FilterChip>
          ))}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-[9/14] animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No templates in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {filtered.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                variant="hero"
                isMuted={isMuted}
                onClick={handleSelect}
                onToggleMuted={() => setIsMuted((current) => !current)}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}

export const Route = createFileRoute("/create/ugc-templates" as any)({
  validateSearch: ugcTemplatesSearchSchema,
  component: UGCTemplatesPage,
})

interface FilterChipProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  )
}
